import { useEffect, useRef } from "react";
import { GLOBE_POINTS } from "@/lib/globePoints";

const C = { x: 360, y: 292 };
const BADGE_W = 158;
const ORBIT_R = 236;

const LABELS = [
  ["Payment Networks", "#0EA5A4"],
  ["Cards", "#2563EB"],
  ["Payment Gateways", "#6A4FBF"],
  ["Wallets", "#0EA5A4"],
  ["FX", "#2563EB"],
  ["Businesses", "#6A4FBF"],
  ["KYC", "#0EA5A4"],
  ["Fraud & Risk", "#2563EB"],
  ["Banks", "#6A4FBF"],
];
const GLOBE_R = 122;

function DottedGlobe() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = canvas.clientWidth || 244;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    const R = size * 0.49;
    const cx = size / 2;
    const colors = GLOBE_POINTS.map((_, i) => {
      const h = (i * 7919) % 100;
      return h < 55 ? "#7FA8EC" : h < 85 ? "#63CFC7" : "#A08FD8";
    });
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let rot = 0.6;
    let last = performance.now();

    const draw = (now) => {
      const dt = now - last;
      last = now;
      if (!reduced) rot += dt * 0.00011;
      ctx.clearRect(0, 0, size, size);
      ctx.beginPath();
      ctx.arc(cx, cx, R + 1, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.fill();
      ctx.strokeStyle = "rgba(37,99,235,0.16)";
      ctx.lineWidth = 1;
      ctx.stroke();
      for (let i = 0; i < GLOBE_POINTS.length; i += 1) {
        const p = GLOBE_POINTS[i];
        const phi = (p[0] * Math.PI) / 180;
        const lam = (p[1] * Math.PI) / 180 + rot;
        const x3 = Math.cos(phi) * Math.sin(lam);
        const y3 = Math.sin(phi);
        const z3 = Math.cos(phi) * Math.cos(lam);
        const x = cx + R * x3;
        const y = cx - R * y3;
        if (z3 > 0.02) {
          ctx.globalAlpha = 0.22 + 0.5 * z3;
          ctx.fillStyle = colors[i];
          ctx.beginPath();
          ctx.arc(x, y, 0.62 + 0.5 * z3, 0, 6.2832);
          ctx.fill();
        } else {
          ctx.globalAlpha = 0.04;
          ctx.fillStyle = "#1D305B";
          ctx.beginPath();
          ctx.arc(x, y, 0.5, 0, 6.2832);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="absolute aspect-square"
      style={{
        left: `${(C.x / 720) * 100}%`,
        top: `${(C.y / 600) * 100}%`,
        width: `${((GLOBE_R * 2) / 720) * 100}%`,
        transform: "translate(-50%, -50%)",
      }}
    />
  );
}

const NODES = LABELS.map(([label, accent], i) => {
  const ang = ((-90 + i * 40) * Math.PI) / 180;
  return { label, accent, x: C.x + ORBIT_R * Math.cos(ang), y: C.y + ORBIT_R * Math.sin(ang) };
});

export default function NetworkHero() {
  return (
    <div
      data-testid="hero-network-visual"
      className="relative w-full"
      role="img"
      aria-label="Rotating dotted globe in iJeeva colours connected to banks, payment networks, gateways, cards, wallets, FX, KYC, fraud and risk systems and businesses"
    >
      <DottedGlobe />
      <svg viewBox="0 0 720 600" className="relative h-auto w-full" aria-hidden="true">
        <defs>
          <radialGradient id="globeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.1" />
            <stop offset="55%" stopColor="#0EA5A4" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#6A4FBF" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0EA5A4" />
            <stop offset="55%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#6A4FBF" />
          </linearGradient>
          <filter id="nodeShadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#0F172A" floodOpacity="0.08" />
          </filter>
        </defs>

        {/* ambient brand glow */}
        <circle cx={C.x} cy={C.y} r="180" fill="url(#globeGlow)" />

        {/* revolving ring: spokes + pulses + badges revolve clockwise; badges counter-rotate to keep text horizontal */}
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`0 ${C.x} ${C.y}`}
            to={`360 ${C.x} ${C.y}`}
            dur="95s"
            repeatCount="indefinite"
          />
          {NODES.map((n, i) => {
            const dx = n.x - C.x;
            const dy = n.y - C.y;
            const len = Math.hypot(dx, dy);
            const sx = C.x + (dx * 130) / len;
            const sy = C.y + (dy * 130) / len;
            return (
              <g key={n.label}>
                <line x1={sx} y1={sy} x2={n.x} y2={n.y} stroke="#E2E8F0" strokeWidth="1.4" />
                <line
                  x1={sx}
                  y1={sy}
                  x2={n.x}
                  y2={n.y}
                  stroke={n.accent}
                  strokeWidth="1.3"
                  strokeDasharray="3 9"
                  opacity="0.45"
                  className="flow-dash"
                />
                <circle r="3.2" fill={n.accent} opacity="0.85">
                  <animateMotion
                    dur={`${9 + (i % 3) * 2}s`}
                    begin={`${i * 1.1}s`}
                    repeatCount="indefinite"
                    path={`M ${sx} ${sy} L ${n.x} ${n.y}`}
                    calcMode="linear"
                  />
                </circle>
                <g filter="url(#nodeShadow)">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from={`0 ${n.x} ${n.y}`}
                    to={`-360 ${n.x} ${n.y}`}
                    dur="95s"
                    repeatCount="indefinite"
                  />
                  <rect x={n.x - BADGE_W / 2} y={n.y - 22} width={BADGE_W} height="44" rx="11" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1.2" />
                  <text x={n.x} y={n.y + 5} textAnchor="middle" fontSize="13.5" fontWeight="500" fill="#1D305B" fontFamily="Inter, sans-serif">
                    {n.label}
                  </text>
                </g>
              </g>
            );
          })}
        </g>

        {/* orbit rings */}
        <circle cx={C.x} cy={C.y} r="158" fill="none" stroke="url(#ringGrad)" strokeWidth="1.3" strokeDasharray="3 10" opacity="0.7" className="flow-dash-slow" />
        <circle cx={C.x} cy={C.y} r="180" fill="none" stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 10" className="flow-dash-slow" />

        {/* orbiting transaction pulses: one shared circular path, evenly spaced */}
        {[
          ["#0EA5A4", 4, "0s"],
          ["#2563EB", 3.6, "-8.67s"],
          ["#6A4FBF", 3.6, "-17.33s"],
        ].map(([color, r, begin]) => (
          <circle key={color} r={r} fill={color}>
            <animateMotion
              dur="26s"
              begin={begin}
              repeatCount="indefinite"
              path={`M ${C.x - 158} ${C.y} a 158 158 0 1 0 316 0 a 158 158 0 1 0 -316 0`}
            />
          </circle>
        ))}

        {/* logo */}
        <rect x={C.x - 64} y={C.y - 24} width="128" height="48" rx="10" fill="#FFFFFF" opacity="0.94" />
        <image href="/logo.png" x={C.x - 55} y={C.y - 18} width="110" height="36" preserveAspectRatio="xMidYMid meet" />
      </svg>
    </div>
  );
}
